import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { debounce } from "lodash";
import {
  getAllMaintenanceSchedule,
  getAllMaintenanceScheduleForSearch,
  getMaintenanceStatistics,
} from "../../redux/MaintenanceScheduleSlice/MaintenanceScheduleSlice";
import ScheduleStatistics from "./ScheduleStatistics";
import {
  isOverdue,
  getStatusText,
  getStatusClass,
  getPriorityText,
  getPriorityClass,
  prepareDataForExport,
  filterByDate,
} from "../../utils/maintenanceScheduleUtils";
import "./MaintenanceSchedulesTable.css";

const MaintenanceSchedulesTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux State
  const {
    allMaintenanceSchedule,
    allDataForSearch,
    statistics,
    status,
    searchDataStatus,
  } = useSelector((state) => state.maintenanceSchedule);

  // Local State
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [dateFilter, setDateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [scheduleType, setScheduleType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  // جلب كل البيانات للبحث مرة واحدة عند تحميل الصفحة
  useEffect(() => {
    dispatch(getAllMaintenanceScheduleForSearch());
  }, [dispatch]);

  // دالة موحدة لتطبيق الفلاتر
  const applyFilters = useCallback(
    (data) => {
      let filtered = [...data];

      // فلتر النوع
      if (scheduleType === "with-fire-extinguishers") {
        filtered = filtered.filter((s) => s.has_fire_extinguishers === true);
      } else if (scheduleType === "without-fire-extinguishers") {
        filtered = filtered.filter((s) => s.has_fire_extinguishers === false);
      }

      // فلتر التاريخ
      filtered = filterByDate(filtered, dateFilter);

      // فلتر الحالة
      if (statusFilter !== "all") {
        filtered = filtered.filter(
          (schedule) => schedule.status === statusFilter,
        );
      }

      return filtered;
    },
    [scheduleType, dateFilter, statusFilter],
  );

  // دالة البحث المحلي
  const performLocalSearch = useCallback(
    (keyword) => {
      const allData = allDataForSearch?.data || [];
      const query = keyword.toLowerCase().trim();

      console.log("البحث عن:", query);
      console.log("إجمالي البيانات:", allData.length);

      // البحث أولاً
      const searchResults = allData.filter((schedule) => {
        const title = schedule.title?.toLowerCase() || "";
        const companyName =
          schedule.contractSchedule?.branch?.company?.name?.toLowerCase() || "";
        const branchName =
          schedule.contractSchedule?.branch?.name?.toLowerCase() || "";
        const contractNumber =
          schedule.contractSchedule?.contract_number?.toLowerCase() || "";
        const notes = schedule.notes?.toLowerCase() || "";
        const status = schedule.status?.toLowerCase() || "";
        const priority = schedule.priority?.toLowerCase() || "";

        return (
          title.includes(query) ||
          companyName.includes(query) ||
          branchName.includes(query) ||
          contractNumber.includes(query) ||
          notes.includes(query) ||
          status.includes(query) ||
          priority.includes(query)
        );
      });

      console.log("نتائج البحث قبل الفلاتر:", searchResults.length);

      // تطبيق الفلاتر على نتائج البحث
      const filtered = applyFilters(searchResults);

      console.log("نتائج البحث بعد الفلاتر:", filtered.length);

      setFilteredSchedules(filtered);
    },
    [allDataForSearch, applyFilters],
  );

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((keyword) => {
      if (keyword.trim() !== "") {
        setIsSearching(true);
        performLocalSearch(keyword);
        setCurrentPage(1);
      } else {
        setIsSearching(false);
        setCurrentPage(1);
      }
    }, 300),
    [performLocalSearch],
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setCurrentPage(1);
  };

  // دالة موحدة لجلب البيانات
  const fetchData = useCallback(
    (page) => {
      dispatch(getAllMaintenanceSchedule(page));
    },
    [dispatch],
  );

  // جلب البيانات عند تحميل الصفحة أو تغيير الصفحة
  useEffect(() => {
    if (!isSearching) {
      fetchData(currentPage);
    }
  }, [currentPage, isSearching, fetchData]);

  // تطبيق الفلاتر عند تغييرها
  useEffect(() => {
    if (isSearching && searchQuery.trim() !== "") {
      // إعادة تطبيق البحث مع الفلاتر الجديدة
      performLocalSearch(searchQuery);
    } else if (!isSearching) {
      // إعادة تعيين الصفحة وجلب البيانات
      setCurrentPage(1);
      fetchData(1);
    }
  }, [scheduleType, dateFilter, statusFilter]);

  // جلب الإحصائيات بناءً على الفلاتر
  useEffect(() => {
    const params = {};

    if (dateFilter === "today") {
      params.period = "daily";
    } else if (dateFilter === "week") {
      params.period = "weekly";
    } else if (dateFilter === "month") {
      params.period = "monthly";
    }

    if (scheduleType === "with-fire-extinguishers") {
      params.has_fire_extinguishers = true;
    } else if (scheduleType === "without-fire-extinguishers") {
      params.has_fire_extinguishers = false;
    }

    if (statusFilter !== "all") {
      params.status = statusFilter;
    }

    dispatch(getMaintenanceStatistics(params));
  }, [dispatch, dateFilter, scheduleType, statusFilter]);

  // تطبيق الفلاتر المحلية على البيانات العادية (بدون بحث)
  useEffect(() => {
    if (!isSearching) {
      const currentSchedules = allMaintenanceSchedule?.data || [];
      const filtered = applyFilters(currentSchedules);
      setFilteredSchedules(filtered);
    }
  }, [allMaintenanceSchedule, isSearching, applyFilters]);

  // دالة التصدير
  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      toast.info("جاري تحضير البيانات للتصدير...");

      // استخدام البيانات المحملة مسبقاً
      let allData = allDataForSearch?.data || [];

      if (allData.length === 0) {
        // إذا البيانات مش محملة، جيبها
        const result = await dispatch(
          getAllMaintenanceScheduleForSearch(),
        ).unwrap();
        allData = result?.data || [];
      }

      if (allData.length === 0) {
        toast.warning("لا توجد بيانات للتصدير");
        return;
      }

      console.log("إجمالي البيانات:", allData.length);

      let filteredData = [...allData];

      // تطبيق البحث أولاً إذا كان موجود
      if (searchQuery.trim() !== "") {
        const query = searchQuery.toLowerCase().trim();
        filteredData = filteredData.filter((schedule) => {
          const title = schedule.title?.toLowerCase() || "";
          const companyName =
            schedule.contractSchedule?.branch?.company?.name?.toLowerCase() ||
            "";
          const branchName =
            schedule.contractSchedule?.branch?.name?.toLowerCase() || "";
          const contractNumber =
            schedule.contractSchedule?.contract_number?.toLowerCase() || "";
          const notes = schedule.notes?.toLowerCase() || "";

          return (
            title.includes(query) ||
            companyName.includes(query) ||
            branchName.includes(query) ||
            contractNumber.includes(query) ||
            notes.includes(query)
          );
        });
      }

      // ثم تطبيق الفلاتر
      filteredData = applyFilters(filteredData);

      console.log("بعد الفلاتر:", filteredData.length);

      if (filteredData.length === 0) {
        toast.warning("لا توجد بيانات تطابق معايير البحث");
        return;
      }

      // تحضير البيانات للتصدير
      const exportData = prepareDataForExport(filteredData);

      // إنشاء الملف
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "جداول الصيانة");

      // اسم الملف
      const fileName = `maintenance_schedules_${format(
        new Date(),
        "yyyy-MM-dd_HH-mm-ss",
      )}.xlsx`;

      XLSX.writeFile(wb, fileName);

      toast.success(
        `تم تصدير ${filteredData.length} جدول من أصل ${allData.length} بنجاح`,
      );
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error(
        "حدث خطأ أثناء التصدير: " + (error.message || "خطأ غير معروف"),
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const loading = status === "loading";
  const statsLoading = status === "loading" && !statistics;
  const searchDataLoading = searchDataStatus === "loading";

  // البيانات والـ pagination
  const pagination = allMaintenanceSchedule?.pagination;
  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.limit)
    : 1;
  const showPagination = !isSearching && pagination && totalPages > 1;

  // البيانات المعروضة
  const displayedSchedules = filteredSchedules;

  return (
    <div className="main">
      <div className="maintenance-schedules-container">
        {/* Header Section */}
        <div className="page-header">
          <h2 className="page-title">جداول الصيانة الدورية</h2>
          <button
            className="btn btn-primary btn-add"
            onClick={() => navigate("/AddMaintenanceSchedule")}
          >
            <i className="fas fa-plus ms-2"></i>
            إضافة جدول جديد
          </button>
        </div>

        {/* Statistics Section */}
        <ScheduleStatistics statistics={statistics} loading={statsLoading} />

        {/* Search Box */}
        <div className="search-section">
          <div className="search-box">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              className="form-control search-input"
              placeholder="ابحث باسم الجدول أو الشركة أو الفرع أو رقم العقد..."
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={searchDataLoading}
            />
            {searchQuery && (
              <button
                className="btn-clear-search"
                onClick={clearSearch}
                title="مسح البحث"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          {isSearching && (
            <div className="search-info text-center mt-2">
              <small className="text-muted">
                <i className="fas fa-search me-1"></i>
                البحث عن: "{searchQuery}" - عدد النتائج:{" "}
                {displayedSchedules.length}
              </small>
            </div>
          )}
          {searchDataLoading && (
            <div className="search-info text-center mt-2">
              <small className="text-muted">
                <i className="fas fa-spinner fa-spin me-1"></i>
                جاري تحميل بيانات البحث...
              </small>
            </div>
          )}
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">نوع الجدول</label>
              <select
                className="form-select"
                value={scheduleType}
                onChange={(e) => setScheduleType(e.target.value)}
              >
                <option value="all">جميع الجداول</option>
                <option value="with-fire-extinguishers">مع طفايات حريق</option>
                <option value="without-fire-extinguishers">
                  بدون طفايات حريق
                </option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">فلتر التاريخ</label>
              <select
                className="form-select"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="all">جميع التواريخ</option>
                <option value="today">اليوم</option>
                <option value="week">هذا الأسبوع</option>
                <option value="month">هذا الشهر</option>
                <option value="overdue">متأخرة</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">الحالة</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">جميع الحالات</option>
                <option value="pending">قيد الانتظار</option>
                <option value="in_progress">جاري التنفيذ</option>
                <option value="completed">مكتمل</option>
                <option value="cancelled">ملغي</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">&nbsp;</label>
              <button
                className="btn btn-success w-100"
                onClick={exportToExcel}
                disabled={isExporting || searchDataLoading}
              >
                {isExporting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm ms-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    جاري التصدير...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-excel ms-2"></i>
                    تصدير الكل إلى Excel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="table-section">
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>عنوان الجدول</th>
                        <th>تاريخ الصيانة</th>
                        <th>الوقت</th>
                        <th>الحالة</th>
                        <th>الأولوية</th>
                        <th>الفني المكلف</th>
                        <th>مدير الصيانة</th>
                        <th>الشركة / الفرع</th>
                        <th>طفايات</th>
                        <th className="actions-column">العمليات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedSchedules.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="text-center py-5">
                            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                            <p className="text-muted">
                              {searchQuery
                                ? "لا توجد نتائج للبحث"
                                : "لا توجد جداول صيانة"}
                            </p>
                            {searchQuery && (
                              <button
                                className="btn btn-sm btn-primary mt-2"
                                onClick={clearSearch}
                              >
                                <i className="fas fa-times me-2"></i>
                                مسح البحث
                              </button>
                            )}
                          </td>
                        </tr>
                      ) : (
                        displayedSchedules.map((schedule) => (
                          <tr
                            key={schedule.id}
                            className={
                              isOverdue(schedule.schedule_date, schedule.status)
                                ? "row-overdue"
                                : ""
                            }
                          >
                            <td>
                              <div className="schedule-title">
                                {schedule.title}
                                {isOverdue(
                                  schedule.schedule_date,
                                  schedule.status,
                                ) && (
                                  <span className="badge bg-danger ms-2">
                                    متأخر
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              {format(
                                new Date(schedule.schedule_date),
                                "yyyy-MM-dd",
                              )}
                            </td>
                            <td>
                              <div className="time-range">
                                {schedule.start_time} - {schedule.end_time}
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge ${getStatusClass(
                                  schedule.status,
                                )}`}
                              >
                                {getStatusText(schedule.status)}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge ${getPriorityClass(
                                  schedule.priority,
                                )}`}
                              >
                                {getPriorityText(schedule.priority)}
                              </span>
                            </td>
                            <td>
                              {schedule.assignedTechnician?.name ||
                                (schedule.assigned_to ? (
                                  <span className="text-muted">
                                    <i className="fas fa-user-check me-1"></i>
                                    معين
                                  </span>
                                ) : (
                                  "-"
                                ))}
                            </td>
                            <td>
                              {schedule.maintenanceManager?.name ||
                                (schedule.maintenance_manager_id ? (
                                  <span className="text-muted">
                                    <i className="fas fa-user-tie me-1"></i>
                                    معين
                                  </span>
                                ) : (
                                  "-"
                                ))}
                            </td>
                            <td>
                              <div className="company-info">
                                <div>
                                  {schedule.contractSchedule?.branch?.company
                                    ?.name || "-"}
                                </div>
                                <small className="text-muted">
                                  {schedule.contractSchedule?.branch?.name ||
                                    "-"}
                                </small>
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  schedule.has_fire_extinguishers
                                    ? "bg-success"
                                    : "bg-secondary"
                                }`}
                              >
                                {schedule.has_fire_extinguishers ? "نعم" : "لا"}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() =>
                                    navigate(
                                      `/ShowMoreMaintenanceSchedule/${schedule.id}`,
                                    )
                                  }
                                  title="عرض التفاصيل"
                                >
                                  <i className="fas fa-eye"></i>
                                </button>

                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() =>
                                    navigate(
                                      `/MaintenanceSchedules/addMangerAndStuff/${schedule.id}`,
                                    )
                                  }
                                  title="توكيل موظف ومدير"
                                >
                                  <i className="fas fa-user-plus"></i>
                                </button>

                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() =>
                                    navigate(
                                      `/AddMaintenanceSchedule/${schedule.id}`,
                                    )
                                  }
                                  title="إضافة عناصر"
                                >
                                  <i className="fas fa-plus-circle"></i>
                                </button>

                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() =>
                                    navigate(
                                      `/MaintenanceSchedules/${schedule.id}/statusMaintenanceItem`,
                                    )
                                  }
                                  title="تقرير الصيانة"
                                >
                                  <i className="fas fa-file-alt"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {showPagination && (
                <div className="pagination-section">
                  <nav>
                    <ul className="pagination justify-content-center">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          السابق
                        </button>
                      </li>

                      {[...Array(totalPages)].map((_, index) => (
                        <li
                          key={index + 1}
                          className={`page-item ${
                            currentPage === index + 1 ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}

                      <li
                        className={`page-item ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          التالي
                        </button>
                      </li>
                    </ul>
                  </nav>

                  <div className="pagination-info text-center mt-2">
                    <small className="text-muted">
                      الصفحة {currentPage} من {totalPages}
                    </small>
                  </div>
                </div>
              )}

              {/* Results Summary */}
              <div className="results-summary">
                <p className="text-muted">
                  {isSearching ? (
                    <>
                      عرض {displayedSchedules.length} نتيجة للبحث عن: "
                      {searchQuery}"
                    </>
                  ) : (
                    <>
                      عرض {displayedSchedules.length} من{" "}
                      {pagination?.total || displayedSchedules.length} جدول
                      صيانة
                    </>
                  )}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceSchedulesTable;
